import { httpQuery } from '@luminovo/data-stack';
import { stringSearch } from '@luminovo/design-system';
import { createFileRoute, notFound, Outlet } from '@tanstack/react-router';

import { AssemblyPcbLayout } from '@/areas/pcb/Pcb/PcbLayout/AssemblyPcbLayout';
import { assembliesModulePermissions } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// The shell of the PCB tabs, so switching tabs keeps the selected document and the PCB websocket updater.
// An assembly the API doesn't know renders the not-found page in place of the shell, which needs it; the loader asks
// with the shell's query. The tab's match stays below, so the navbar, route name and page view are the tab's.
export const Route = createFileRoute('/_authenticated/assemblies/$assemblyId/pcb')({
    // The same access as its tabs; the navbar reads the tab's chrome.
    staticData: {
        access: { permissions: ['view:assembly'], modulePermissions: assembliesModulePermissions },
        chrome: 'none',
    } satisfies PageStaticData,
    // A layout's `Route.useSearch` is typed from its own `validateSearch` and its parents', not its tabs'.
    validateSearch: stringSearch({ rfqId: 'required' }),
    loader: async ({ context: { queryClient }, params: { assemblyId } }) => {
        const { items } = await queryClient.ensureQueryData(
            httpQuery('POST /assemblies/bulk', {
                requestBody: { ids: [assemblyId] },
                queryParams: { with_aggregations: false },
            }),
        );
        if (items.length === 0) {
            throw notFound();
        }
    },
    component: RouteComponent,
    // The shell's state belongs to one assembly's PCB; the router would keep it across a param change.
    remountDeps: ({ params }) => params.assemblyId,
});

function RouteComponent(): JSX.Element {
    const { assemblyId } = Route.useParams();
    const rfqId = Route.useSearch({ select: (search) => search.rfqId });
    return (
        <AssemblyPcbLayout assemblyId={assemblyId} rfqId={rfqId}>
            <Outlet />
        </AssemblyPcbLayout>
    );
}
